/**
 * A LETRA CABE NO BALÃO — `T1.6`.
 *
 * Ele, na letra: *"os balões de fala não funcionam direito — são só PNG com texto por cima; **se o
 * texto é grande, vaza pra fora do balão**. Fonte genérica sans. Ferramenta central de HQ está
 * quebrada."*
 *
 * ── O QUE FOI MEDIDO, EM 27/09, NO EDITOR ───────────────────────────────────────────────────
 *
 * Texto crescente num balão de 150×150, lendo a caixa da letra rasterizada a cada passo:
 *
 *     2 caracteres    letra  15×20
 *    13 caracteres    letra  99×20
 *    38 caracteres    letra **150**×40    ← a letra já ocupa a largura INTEIRA da forma
 *    88 caracteres    letra **150**×100
 *   143 caracteres    recusado pelo produto, que já tinha teto de 100
 *
 * **"Vaza pra fora do balão" não reproduz como transbordo da caixa:** o texto quebra linha e fica
 * dentro dela. O que ele atravessa é a **forma** — a caixa do balão é um retângulo e o balão
 * desenhado dentro dela é arredondado, então texto que usa a largura inteira do retângulo passa por
 * cima da curva nos cantos.
 *
 * ── E O CONSERTO ÓBVIO FOI MEDIDO E DESCARTADO ──────────────────────────────────────────────
 *
 * Estreitar a largura útil para 78% pôs a letra em **117×160** numa forma de 150×150: a largura
 * menor faz o texto quebrar em mais linhas, e as linhas passam da ALTURA. Uma margem troca um
 * transbordo pelo outro.
 *
 * Então o conserto é a letra CABER: o corpo da fonte cai até o texto entrar na área útil. A forma
 * não muda de tamanho — e isso é deliberado, porque mudar o tamanho do item dentro do efeito que o
 * desenha é mutação de estado no laço de renderização do editor, que este produto já registrou como
 * remontando a cada mudança (`T4.9`).
 *
 * ── POR QUE ESTA FUNÇÃO É PURA, E MEDE EM VEZ DE ESTIMAR ────────────────────────────────────
 *
 * Ela recebe as duas caixas — a que o texto ocupa e a que ele pode ocupar — e devolve o fator. Quem
 * mede o texto é o DOM, porque quebra de linha depende da fonte carregada e de onde as palavras
 * cabem: estimar por contagem de caracteres erra em "Mmmm" e em "iiii" pelo mesmo motivo.
 *
 * E ela tem um PISO: abaixo de um certo corpo a letra deixa de ser legível para quem tem oito anos,
 * e um balão ilegível é pior que um balão com a letra tocando a borda. Quando o piso não basta, a
 * função diz isso no retorno em vez de esconder — e quem chama decide.
 */

/** A fração da caixa do balão em que a letra pode entrar, por eixo. */
export const AREA_UTIL_DO_BALAO = { largura: 0.78, altura: 0.7 };

/**
 * O menor corpo de fonte que ainda se lê num balão de quadrinho, em px.
 *
 * Não é um número de gosto: abaixo de 9 px a letra de uma fonte arredondada como a Fredoka perde o
 * contraste interno dos contornos, e quem lê é criança em tela de escola.
 */
export const CORPO_MINIMO = 9;

/**
 * O maior corpo que um balão de fala usa, em px.
 *
 * Teto, e não gosto: acima disto a letra deixa de ser fala e passa a ser título. Ele também protege
 * contra medição ruim — um `ocupa` minúsculo (a fonte ainda não carregou, o texto ainda tem uma
 * letra só) daria um fator enorme.
 */
export const CORPO_MAXIMO = 72;

/**
 * A folga em que o corpo já é considerado do tamanho certo.
 *
 * Sem ela o laço de `caberNoBalao` não sairia: como o corpo agora também CRESCE, o fator quase nunca
 * é exatamente 1, e duas passadas ficariam trocando meio pixel de corpo entre si.
 */
export const TOLERANCIA = 0.02;

export type Caixa = { largura: number; altura: number };

export type Ajuste = {
  /** O multiplicador a aplicar no corpo da fonte. 1 quando já cabia. */
  fator: number;
  /** O corpo final, em px, já com o piso aplicado. */
  corpo: number;
  /** `true` quando nem no piso o texto entra — quem chama decide o que fazer. */
  noPiso: boolean;
};

/**
 * Quanto o corpo da fonte tem de mudar para o texto PREENCHER a área útil.
 *
 * `ocupa` é a caixa que o texto ocupa HOJE, medida no DOM; `disponivel` é a caixa do balão. O fator
 * sai do eixo mais apertado dos dois, porque mudar só um eixo não existe em tipografia.
 *
 * ── A LETRA PASSOU A CRESCER, E NÃO SÓ A ENCOLHER — `T5.10` ─────────────────────────────────
 *
 * Ele, na letra: *"não está mantendo a formatação. Não mantém a formatação interna dentro do
 * negócio, no meu quadro (...) **a preview não significa que é o formato correto**"*.
 *
 * MEDIDO em 28/09 (`rastro/medir-a-letra-que-reescala.mjs`), arrastando a alça do balão em oito
 * passos de 19 px:
 *
 *     a caixa do balão ......... 146×146 -> 298×298   (×2,04)
 *     a forma desenhada ........ acompanhou            (×2,04)
 *     a imagem do TEXTO ........ 98×19  ->  98×19     (×1,00)   ← não acompanhou
 *
 * A causa estava AQUI, e não nas dependências do efeito que rasteriza: esta função devolvia
 * `fator: 1` sempre que o texto já cabia (`if (cabe >= 1) return ...`). Então a caixa crescia, o
 * texto continuava caber, e nada mudava. Foi por isso que a tentativa de 27/09 — que só acrescentou
 * dependências — não mexeu no bitmap, e foi revertida por não conseguir medir efeito nenhum.
 *
 * ── A DECISÃO, E O CUSTO DE ELA ESTAR ERRADA ────────────────────────────────────────────────
 *
 * São duas leis possíveis para a letra, e as duas são defensáveis:
 *
 *   A) a letra CRESCE com a caixa — o balão inteiro escala, como uma figura;
 *   B) a letra mantém o corpo e RE-QUEBRA as linhas — a caixa maior só ganha mais espaço em volta.
 *
 * Escolhi a A, e o argumento não é de gosto: a **forma** do balão já escala ×2,04. Com a lei B, um
 * mesmo objeto se comporta de duas maneiras ao mesmo tempo — a arte acompanha e a letra não —, e é
 * exatamente isso que ele viu e chamou de "não mantém a formatação".
 *
 * CUSTO DE ESTAR ERRADO: balões já salvos cujo texto era pequeno em relação à caixa passam a
 * aparecer com letra maior ao reabrir. Nada se perde (o texto é guardado como texto, não como
 * imagem), e voltar para a lei B é uma linha — devolver o `if (cabe >= 1) return fator 1`. Se o olho
 * dele preferir a B, a reversão é essa e está escrita aqui de propósito.
 */
export function ajusteParaCaber(ocupa: Caixa, disponivel: Caixa, corpoAtual: number): Ajuste {
  const util = {
    largura: disponivel.largura * AREA_UTIL_DO_BALAO.largura,
    altura: disponivel.altura * AREA_UTIL_DO_BALAO.altura,
  };

  // Caixa vazia ou medida inválida: não há o que ajustar, e inventar fator aqui poria a letra em
  // zero no primeiro render, antes de o texto existir.
  if (!ocupa.largura || !ocupa.altura || !util.largura || !util.altura || !corpoAtual) {
    return { fator: 1, corpo: corpoAtual, noPiso: false };
  }

  const cabe = Math.min(util.largura / ocupa.largura, util.altura / ocupa.altura);
  // JÁ ESTÁ DO TAMANHO CERTO — e a folga é o que impede o laço de girar (ver `TOLERANCIA`).
  if (Math.abs(cabe - 1) < TOLERANCIA) return { fator: 1, corpo: corpoAtual, noPiso: false };

  // O corpo segue o `cabe` nos DOIS sentidos: menor que 1 encolhe, maior que 1 cresce. O piso vale
  // só na descida e o teto só na subida — e o `noPiso` continua sendo sobre não caber, nunca sobre
  // não crescer, porque letra pequena numa caixa grande é feia e letra ilegível é outra coisa.
  const corpoIdeal = corpoAtual * cabe;
  const corpo = cabe < 1 ? Math.max(CORPO_MINIMO, corpoIdeal) : Math.min(CORPO_MAXIMO, corpoIdeal);
  return { fator: corpo / corpoAtual, corpo, noPiso: cabe < 1 && corpoIdeal < CORPO_MINIMO };
}

/**
 * Aplica o ajuste no elemento, medindo até caber — no máximo `TENTATIVAS` vezes.
 *
 * ── POR QUE ITERAR, E POR QUE SÓ TRÊS VEZES ─────────────────────────────────────────────────
 *
 * Um fator calculado de uma medição não garante o encaixe, porque **encolher a letra muda onde as
 * linhas quebram**: quatro linhas podem virar três, e a altura cai mais do que o fator previa. Duas
 * a três passadas convergem, e o teto existe para que um caso patológico pare em vez de girar.
 *
 * E ELA MEXE NO DOM DIRETO, de propósito: se o corpo fosse estado de React, cada passada seria um
 * render novo do editor inteiro — e o efeito que chama isto roda antes de rasterizar o texto. Aqui a
 * medição e o ajuste acontecem dentro da mesma passada, e o React não fica sabendo.
 */
export const TENTATIVAS = 3;

export function caberNoBalao(
  el: HTMLElement,
  disponivel: Caixa,
  corpoBase: number,
): Ajuste {
  let ultimo: Ajuste = { fator: 1, corpo: corpoBase, noPiso: false };
  for (let i = 0; i < TENTATIVAS; i++) {
    const ocupa = { largura: el.offsetWidth, altura: el.offsetHeight };
    const a = ajusteParaCaber(ocupa, disponivel, ultimo.corpo);
    // A folga, e não a igualdade: com crescimento o fator quase nunca é exatamente 1.
    if (Math.abs(a.fator - 1) < TOLERANCIA) return ultimo;
    ultimo = { fator: (a.corpo / corpoBase), corpo: a.corpo, noPiso: a.noPiso };
    el.style.fontSize = `${a.corpo}px`;
    // Ler `offsetHeight` força o navegador a refazer o layout, e é o que torna a próxima medição
    // verdadeira em vez de repetida.
    void el.offsetHeight;
    if (a.noPiso) return ultimo;
  }
  return ultimo;
}
